import React, { useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { RechargeCarte } from '../../../utils/types/RechargeCarte';
import * as XLSX from 'xlsx';
import { TRechargeCarteMaster } from '../../../utils/types/tables/TRechargeCarteMaster';

ModuleRegistry.registerModules([AllCommunityModule]);

interface RechargeCarteMasterGridProps {
    recharges: TRechargeCarteMaster[];
    onCancel?: () => void;
}

const RechargeCarteMasterGrid: React.FC<RechargeCarteMasterGridProps> = ({ recharges, onCancel }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [columnDefs] = useState<ColDef<TRechargeCarteMaster>[]>([
        {
            field: 'dateAllocation',
            headerName: 'Date',
            sortable: true,
            filter: 'agDateColumnFilter',
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy HH:mm') : ''),
        },
        {
            headerName: 'Val. ($) / Qté (L)',
            sortable: true,
            filter: 'agNumberColumnFilter',
            cellStyle: { textAlign: 'right' },
            valueGetter: (params) => (params.data?.typeAllocation === 'Quantité' ? params.data.qteTotal : params.data?.valeurUsd),
            valueFormatter: (params) => {
                const val = params.value;
                if (val == null) return '';
                return params.data?.typeAllocation === 'Quantité' ? `${(val as number).toFixed(2)} L` : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val as number);
            },
        },
    ]);

    const myTheme = themeBalham.withParams({ columnBorder: { style: 'dashed', width: '1px' } });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const keys = visibleCols.map((col) => col.getColId());
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        gridRef.current.api.exportDataAsCsv({
            fileName: `recharges_carte_${ts}.csv`,
            allColumns: false,
            columnKeys: keys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;
        const visibleCols = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleCols || visibleCols.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }
        const data: TRechargeCarteMaster[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => data.push(node.data));
        if (data.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const nodes: any[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => nodes.push(node));
        if (nodes.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        const exportData = nodes.map((node) => {
            const row = node.data as RechargeCarte;
            const obj: Record<string, any> = {};
            visibleCols.forEach((col) => {
                const def = col.getColDef();
                const header = def.headerName ?? def.field ?? '';
                let value: any;

                if (typeof def.valueGetter === 'function') {
                    value = def.valueGetter({
                        data: row,
                        node,
                        colDef: def,
                        column: col,
                        api: gridRef.current!.api,
                        context: null,
                        getValue: (field: string) => (row as any)[field],
                    } as any);
                } else if (def.field) {
                    value = (row as any)[def.field];
                }

                if (def.field === 'dateAllocation') {
                    value = value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '';
                } else if (typeof value === 'number') {
                    value = value.toFixed(2);
                }

                obj[header] = value;
            });
            return obj;
        });

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recharges');
        const ts = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(wb, `recharges_carte_${ts}.xlsx`);
    }, []);

    return (
        <>
            <div style={{ height: 500, width: '100%' }}>
                <AgGridReact ref={gridRef} theme={myTheme} getRowId={(params) => params.data.id.toString()} rowData={recharges} columnDefs={columnDefs} pagination={true} domLayout="normal" defaultColDef={{ resizable: true, editable: false, minWidth: 100, flex: 1 }} suppressExcelExport={true} enableCellTextSelection={true} ensureDomOrder={true} animateRows={true} overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>" />
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default RechargeCarteMasterGrid;
